/* eslint react/no-multi-comp: 0 */
import React from 'react';

import {
    DangerButtonExample,
    DefaultButtonExample,
    DisabledButtonExample,
    GhostButtonExample,
    LoadingButtonExample,
    LongTextButtonExample,
} from '@ui/components/Button/Button.examples';
import {
    ActionSlotCardExample,
    DefaultCardExample,
    LoadingCompatibleCardExample,
    LongContentCardExample,
    TitledCardExample,
} from '@ui/components/Card/Card.examples';
import {
    ActionAlertBannerExample,
    ErrorAlertBannerExample,
    InfoAlertBannerExample,
    LongContentAlertBannerExample,
    SuccessAlertBannerExample,
    WarningAlertBannerExample,
} from '@ui/components/AlertBanner/AlertBanner.examples';
import {
    CheckedCheckboxExample,
    DisabledCheckboxExample,
    GroupCheckboxExample,
    IndeterminateCheckboxExample,
    UncheckedCheckboxExample,
} from '@ui/components/Checkbox/Checkbox.examples';
import {
    ActionSlotEmptyStateExample,
    MessageOnlyEmptyStateExample,
    NoConfiguredItemsEmptyStateExample,
    NoRecordsEmptyStateExample,
    NoSearchResultsEmptyStateExample,
} from '@ui/components/EmptyState/EmptyState.examples';
import {
    ActiveHeaderTabsExample,
    DefaultHeaderTabsExample,
    DisabledHeaderTabsExample,
    IconHeaderTabsExample,
    LongLabelHeaderTabsExample,
} from '@ui/components/HeaderTabs/HeaderTabs.examples';
import {
    ComposedFieldFormGroupExample,
    ErrorFormGroupExample,
    LabelAndHintFormGroupExample,
    LabelOnlyFormGroupExample,
    RequiredFormGroupExample,
} from '@ui/components/FormGroup/FormGroup.examples';
import ReloFormGroup from '@ui/components/sports-components/FormGroup';
import ReloFormGroupLabel from '@ui/components/sports-components/FormGroupLabel';
import ReloFormGroupHint from '@ui/components/sports-components/FormGroupHint';
import {
    DefaultSelectExample,
    DisabledSelectExample,
    EmptySelectExample,
    LoadingSelectExample,
    LongOptionListSelectExample,
    MultipleSelectExample,
} from '@ui/components/Select/Select.examples';
import {
    DefaultSearchSelectExample,
    DisabledSearchSelectExample,
    LoadingSearchSelectExample,
    NoResultsSearchSelectExample,
    SearchFilterSearchSelectExample,
} from '@ui/components/SearchSelect/SearchSelect.examples';
import {
    CustomPresetsDateRangePickerExample,
    DefaultDateRangePickerExample,
    DisabledDateRangePickerExample,
    EmptyDateRangePickerExample,
    EndTodayDateRangePickerExample,
    FixedDateRangePickerExample,
} from '@ui/components/DateRangePicker/DateRangePicker.examples';
import {
    DefaultLoadingDotsExample,
    InlineLoadingDotsExample,
    LargeLoadingDotsExample,
    SmallLoadingDotsExample,
    WhiteLoadingDotsExample,
} from '@ui/components/LoadingDots/LoadingDots.examples';
import {
    DefaultSpinExample,
    LargeSpinExample,
    SmallSpinExample,
    TipSpinExample,
    WrappedContentSpinExample,
} from '@ui/components/Spin/Spin.examples';
import {
    ActiveSlabNavExample,
    BrandToneSlabNavExample,
    DefaultSlabNavExample,
    DisabledSlabNavExample,
    ManyItemsSlabNavExample,
} from '@ui/components/SlabNav/SlabNav.examples';
import ReloSlabNavContainer from '@ui/components/sports-components/SlabNavContainer';
import ReloSlabNavItem from '@ui/components/sports-components/SlabNavItem';
import {
    DefaultTooltipExample,
    DisabledTriggerTooltipExample,
    LongContentTooltipExample,
    PlacementsTooltipExample,
} from '@ui/components/Tooltip/Tooltip.examples';
import {
    ClosableTagExample,
    ColorsTagExample,
    DefaultTagExample,
    LongTextTagExample,
} from '@ui/components/Tag/Tag.examples';
import {
    DefaultOpenAccordionExample,
    DisabledAccordionExample,
    IconAccordionExample,
    MultipleExpandAccordionExample,
    NestedChildContentAccordionExample,
    SingleExpandAccordionExample,
} from '@ui/components/Accordion/Accordion.examples';
import ReloAccordionContainer from '@ui/components/sports-components/AccordionContainer';
import ReloAccordionList from '@ui/components/sports-components/AccordionList';
import ReloAccordionItem from '@ui/components/sports-components/AccordionItem';
import ReloAccordionTitle from '@ui/components/sports-components/AccordionTitle';
import ReloAccordionIcon from '@ui/components/sports-components/AccordionIcon';
import ReloAccordionChildItems from '@ui/components/sports-components/AccordionChildItems';
import {
    AntdAlert,
    AntdButton,
    AntdCard,
    AntdCheckbox,
    AntdEmpty,
    AntdSearchSelect,
    AntdSelect,
    AntdSpin,
    AntdTabs,
    AntdTag,
    AntdTooltip,
    Input,
    Select,
    Skeleton,
} from '@ui/components';

const headerTabsItems = [
    { key: 'overview', label: 'Overview' },
    { key: 'broadcasts', label: 'Broadcasts' },
    { key: 'social', label: 'Social' },
];

export interface UiCatalogExample {
    name: string;
    description: string;
    Example: React.ComponentType;
    ReferenceExample?: React.ComponentType;
}

export interface UiCatalogSection {
    name: string;
    description: string;
    examples: UiCatalogExample[];
    /** Label for the reference column; defaults to "Raw AntD reference". */
    referenceLabel?: string;
}

const DefaultButtonReference = () => <AntdButton>Default action</AntdButton>;
const DisabledButtonReference = () => (
    <AntdButton disabled type="default">
        Disabled action
    </AntdButton>
);
const LoadingButtonReference = () => (
    <AntdButton loading type="primary">
        Loading action
    </AntdButton>
);
const DangerButtonReference = () => (
    <AntdButton danger type="primary">
        Delete report
    </AntdButton>
);
const GhostButtonReference = () => (
    <AntdButton ghost type="default">
        Ghost action
    </AntdButton>
);
const LongTextButtonReference = () => (
    <AntdButton type="default">
        Review all selected media value center placements before exporting
    </AntdButton>
);

const DefaultCardReference = () => (
    <AntdCard>Use cards for generic grouped MVC content.</AntdCard>
);
const TitledCardReference = () => (
    <AntdCard title="Campaign summary">Campaign details go here.</AntdCard>
);
const ActionSlotCardReference = () => (
    <AntdCard
        actions={[<AntdButton key="view-details">View details</AntdButton>]}
    >
        Content with a caller-owned action slot.
    </AntdCard>
);
const LongContentCardReference = () => (
    <AntdCard title="Long content">
        Cards can contain longer explanatory content without owning feature
        workflow, routing, sponsor, rightsholder, report, or placement behavior.
    </AntdCard>
);
const LoadingCompatibleCardReference = () => (
    <AntdCard title="Loading state">
        <Skeleton active paragraph={{ rows: 3 }} />
    </AntdCard>
);

const InfoAlertBannerReference = () => (
    <AntdAlert showIcon message="Sync scheduled for tonight." type="info" />
);
const WarningAlertBannerReference = () => (
    <AntdAlert
        showIcon
        message="Some placements are missing rightsholder data."
        type="warning"
    />
);
const ErrorAlertBannerReference = () => (
    <AntdAlert
        showIcon
        message="Report generation failed. Try again."
        type="error"
    />
);
const SuccessAlertBannerReference = () => (
    <AntdAlert showIcon message="Campaign saved successfully." type="success" />
);
const LongContentAlertBannerReference = () => (
    <AntdAlert
        showIcon
        description="This view is showing partial data because some sources have not finished aggregating. Inline banners communicate persistent page or card feedback and do not own toast, snackbar, or global notification behavior."
        message="Partial data"
        type="warning"
    />
);
const ActionAlertBannerReference = () => (
    <AntdAlert
        showIcon
        action={<AntdButton>Retry</AntdButton>}
        message="Export failed"
        type="error"
    />
);

const NoRecordsEmptyStateReference = () => (
    <AntdEmpty description="No campaigns yet" />
);
const NoSearchResultsEmptyStateReference = () => (
    <AntdEmpty description="No results" />
);
const NoConfiguredItemsEmptyStateReference = () => (
    <AntdEmpty description="No placements configured" />
);
const ActionSlotEmptyStateReference = () => (
    <AntdEmpty description="No campaigns yet">
        <AntdButton type="primary">Create campaign</AntdButton>
    </AntdEmpty>
);
const MessageOnlyEmptyStateReference = () => (
    <AntdEmpty description="No records found." />
);

const DefaultHeaderTabsReference = () => <AntdTabs items={headerTabsItems} />;
const ActiveHeaderTabsReference = () => (
    <AntdTabs activeKey="broadcasts" items={headerTabsItems} />
);
const DisabledHeaderTabsReference = () => (
    <AntdTabs
        defaultActiveKey="overview"
        items={[
            { key: 'overview', label: 'Overview' },
            { key: 'broadcasts', label: 'Broadcasts' },
            { disabled: true, key: 'reports', label: 'Reports' },
        ]}
    />
);
const IconHeaderTabsReference = () => (
    <AntdTabs
        defaultActiveKey="overview"
        items={[
            {
                icon: <span aria-hidden>📊</span>,
                key: 'overview',
                label: 'Overview',
            },
            {
                icon: <span aria-hidden>📺</span>,
                key: 'broadcasts',
                label: 'Broadcasts',
            },
        ]}
    />
);
const LongLabelHeaderTabsReference = () => (
    <AntdTabs
        defaultActiveKey="annual"
        items={[
            { key: 'annual', label: 'Annual sponsorship media value summary' },
            {
                key: 'quarterly',
                label: 'Quarterly broadcast exposure breakdown',
            },
        ]}
    />
);

const formGroupMetricOptions = [
    { label: 'Impressions', value: 'impressions' },
    { label: 'Media value', value: 'media-value' },
    { label: 'Engagements', value: 'engagements' },
];

const LabelOnlyFormGroupReference = () => (
    <ReloFormGroup>
        <ReloFormGroupLabel text="Social metric" />
        <Select
            options={formGroupMetricOptions}
            placeholder="Select a metric"
        />
    </ReloFormGroup>
);
const LabelAndHintFormGroupReference = () => (
    <ReloFormGroup>
        <ReloFormGroupLabel text="Campaign name" />
        <Input placeholder="Spring sponsorship" />
        <ReloFormGroupHint>
            Shown on exported reports and dashboards.
        </ReloFormGroupHint>
    </ReloFormGroup>
);
const RequiredFormGroupReference = () => (
    <ReloFormGroup>
        <ReloFormGroupLabel text="Rightsholder" />
        <Select
            options={formGroupMetricOptions}
            placeholder="Select a rightsholder"
        />
    </ReloFormGroup>
);
const ErrorFormGroupReference = () => (
    <ReloFormGroup context="danger">
        <ReloFormGroupLabel text="Sponsor" />
        <Select
            options={formGroupMetricOptions}
            placeholder="Select a sponsor"
        />
        <ReloFormGroupHint>
            A sponsor is required to continue.
        </ReloFormGroupHint>
    </ReloFormGroup>
);
const ComposedFieldFormGroupReference = () => (
    <ReloFormGroup>
        <ReloFormGroupLabel text="Goal target" />
        <Input placeholder="0" />
        <ReloFormGroupHint>
            Enter the target value the pacing goal should reach.
        </ReloFormGroupHint>
    </ReloFormGroup>
);

const UncheckedCheckboxReference = () => (
    <AntdCheckbox>Include in report</AntdCheckbox>
);
const CheckedCheckboxReference = () => (
    <AntdCheckbox defaultChecked>Include in report</AntdCheckbox>
);
const IndeterminateCheckboxReference = () => (
    <AntdCheckbox indeterminate>Some placements selected</AntdCheckbox>
);
const DisabledCheckboxReference = () => (
    <AntdCheckbox disabled>Locked selection</AntdCheckbox>
);
const GroupCheckboxReference = () => (
    <AntdCheckbox.Group
        defaultValue={['broadcast']}
        options={[
            { label: 'Broadcast', value: 'broadcast' },
            { label: 'Social', value: 'social' },
            { disabled: true, label: 'Print', value: 'print' },
        ]}
    />
);

const selectFruitOptions = [
    { label: 'Apple', value: 'apple' },
    { label: 'Banana', value: 'banana' },
    { label: 'Cherry', value: 'cherry' },
];
const selectLongOptions = Array.from({ length: 50 }, (_, index) => ({
    label: `Option ${index + 1}`,
    value: `option-${index + 1}`,
}));

const DefaultSelectReference = () => (
    <AntdSelect
        options={selectFruitOptions}
        placeholder="Select a fruit"
        style={{ width: '100%' }}
    />
);
const MultipleSelectReference = () => (
    <AntdSelect
        mode="multiple"
        options={selectFruitOptions}
        placeholder="Select fruits"
        style={{ width: '100%' }}
    />
);
const DisabledSelectReference = () => (
    <AntdSelect
        disabled
        options={selectFruitOptions}
        placeholder="Disabled"
        style={{ width: '100%' }}
    />
);
const LoadingSelectReference = () => (
    <AntdSelect
        loading
        options={selectFruitOptions}
        placeholder="Loading options"
        style={{ width: '100%' }}
    />
);
const LongOptionListSelectReference = () => (
    <AntdSelect
        options={selectLongOptions}
        placeholder="Select an option"
        showSearch
        style={{ width: '100%' }}
    />
);
const EmptySelectReference = () => (
    <AntdSelect
        options={[]}
        placeholder="No options available"
        style={{ width: '100%' }}
    />
);

const DefaultSpinReference = () => <AntdSpin />;
const SmallSpinReference = () => <AntdSpin size="small" />;
const LargeSpinReference = () => <AntdSpin size="large" />;
const TipSpinReference = () => (
    <AntdSpin tip="Loading campaigns…">
        <div style={{ minHeight: 80, padding: 16 }}>
            Content becomes available once loading completes.
        </div>
    </AntdSpin>
);
const WrappedContentSpinReference = () => (
    <AntdSpin spinning>
        <div
            style={{
                background: '#f5f7fb',
                borderRadius: 6,
                minHeight: 96,
                padding: 16,
            }}
        >
            Wrapped content is blurred while the spinner is active.
        </div>
    </AntdSpin>
);

const searchSelectReferenceOptions = [
    { label: 'Premier League', value: 1 },
    { label: 'La Liga', value: 2 },
    { label: 'Serie A', value: 3 },
    { label: 'Bundesliga', value: 4 },
    { label: 'Ligue 1', value: 5 },
];

const DefaultSearchSelectReference = () => (
    <AntdSearchSelect
        options={searchSelectReferenceOptions}
        placeholder="Select a rightsholder"
        showSearch
        style={{ width: 240 }}
    />
);
const SearchFilterSearchSelectReference = () => (
    <AntdSearchSelect
        defaultValue={2}
        options={searchSelectReferenceOptions}
        placeholder="Type to filter leagues"
        showSearch
        style={{ width: 240 }}
    />
);
const NoResultsSearchSelectReference = () => (
    <AntdSearchSelect
        options={[]}
        placeholder="No leagues configured"
        showSearch
        style={{ width: 240 }}
    />
);
const DisabledSearchSelectReference = () => (
    <AntdSearchSelect
        defaultValue={1}
        disabled
        options={searchSelectReferenceOptions}
        placeholder="Disabled rightsholder filter"
        showSearch
        style={{ width: 240 }}
    />
);
const LoadingSearchSelectReference = () => (
    <AntdSearchSelect
        loading
        options={[]}
        placeholder="Loading leagues"
        showSearch
        style={{ width: 240 }}
    />
);

const reloSlabMetrics = [
    { key: 'exposures', label: 'Exposures', icon: 'paper-plane fal' },
    { key: 'impressions', label: 'Impressions', icon: 'eye fal' },
    { key: 'engagements', label: 'Engagements', icon: 'thumbs-up fal' },
    { key: 'mediaValue', label: 'Media Value', icon: 'dollar-sign fal' },
];

const DefaultSlabNavReference = () => (
    <ReloSlabNavContainer vertical justify>
        {reloSlabMetrics.slice(0, 3).map(({ key, label, icon }) => (
            <ReloSlabNavItem
                key={key}
                icon={icon}
                label={label}
                size="xs"
                vertical
                justify
            />
        ))}
    </ReloSlabNavContainer>
);
const ActiveSlabNavReference = () => (
    <ReloSlabNavContainer vertical justify>
        {reloSlabMetrics.map(({ key, label, icon }) => (
            <ReloSlabNavItem
                key={key}
                icon={icon}
                label={label}
                size="xs"
                vertical
                justify
                selected={key === 'impressions'}
            />
        ))}
    </ReloSlabNavContainer>
);
const DisabledSlabNavReference = () => (
    <ReloSlabNavContainer vertical justify>
        <ReloSlabNavItem
            icon="thumbs-up fal"
            label="Engagements"
            size="xs"
            vertical
            justify
            selected
        />
        <ReloSlabNavItem
            icon="lock fal"
            label="Sponsor Value"
            size="xs"
            vertical
            justify
        />
    </ReloSlabNavContainer>
);
const ManyItemsSlabNavReference = () => (
    <ReloSlabNavContainer vertical justify>
        {Array.from({ length: 8 }, (_, index) => (
            <ReloSlabNavItem
                key={`item-${index}`}
                icon="chart-line fal"
                label={`Metric ${index + 1}`}
                size="xs"
                vertical
                justify
                selected={index === 0}
            />
        ))}
    </ReloSlabNavContainer>
);

const tooltipTriggerStyle: React.CSSProperties = {
    borderBottom: '1px dashed currentColor',
    cursor: 'help',
};

const DefaultTooltipReference = () => (
    <AntdTooltip title="Media value details">
        <span style={tooltipTriggerStyle}>Hover for details</span>
    </AntdTooltip>
);
const PlacementsTooltipReference = () => (
    <span
        style={{
            alignItems: 'center',
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 32,
            padding: '24px 0',
        }}
    >
        {(['top', 'right', 'bottom', 'left'] as const).map(placement => (
            <AntdTooltip
                key={placement}
                placement={placement}
                title={`Placement: ${placement}`}
            >
                <span style={tooltipTriggerStyle}>{placement}</span>
            </AntdTooltip>
        ))}
    </span>
);
const DisabledTriggerTooltipReference = () => (
    <AntdTooltip title="This action is unavailable for the current report">
        <span style={{ cursor: 'not-allowed', display: 'inline-block' }}>
            <button disabled type="button">
                Disabled action
            </button>
        </span>
    </AntdTooltip>
);
const LongContentTooltipReference = () => (
    <AntdTooltip title="Tooltips can carry a longer explanatory sentence, wrapping onto multiple lines while staying anchored to the trigger element.">
        <span style={tooltipTriggerStyle}>Long content</span>
    </AntdTooltip>
);

const DefaultTagReference = () => <AntdTag>Default</AntdTag>;
const ColorsTagReference = () => (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}>
        <AntdTag color="success">Success</AntdTag>
        <AntdTag color="processing">Processing</AntdTag>
        <AntdTag color="warning">Warning</AntdTag>
        <AntdTag color="error">Error</AntdTag>
        <AntdTag color="blue">Blue</AntdTag>
    </span>
);
const ClosableTagReference = () => <AntdTag closable>Closable</AntdTag>;
const LongTextTagReference = () => (
    <AntdTag style={{ maxWidth: 220, whiteSpace: 'normal' }}>
        A longer tag label that shows how the tag handles extended content
    </AntdTag>
);

// Legacy @relo gds-accordion reference column (rendered beside the native family
// so reviewers can compare). Uses the same caller-owned composition the native
// family preserves.
const reloAccordionSections = [
    { key: 'a', title: 'Section A', body: 'Content for section A.' },
    { key: 'b', title: 'Section B', body: 'Content for section B.' },
    { key: 'c', title: 'Section C', body: 'Content for section C.' },
];

const SingleExpandAccordionReference = () => {
    const [openKey, setOpenKey] = React.useState<string | null>('a');

    return (
        <ReloAccordionContainer>
            <ReloAccordionList>
                {reloAccordionSections.map(section => {
                    const isActive = openKey === section.key;
                    const toggle = () =>
                        setOpenKey(isActive ? null : section.key);

                    return (
                        <ReloAccordionItem
                            key={section.key}
                            isActive={isActive}
                        >
                            <ReloAccordionTitle onClick={toggle}>
                                {section.title}
                            </ReloAccordionTitle>
                            <ReloAccordionIcon size="sm" onClick={toggle} />
                            {isActive && (
                                <ReloAccordionChildItems>
                                    {section.body}
                                </ReloAccordionChildItems>
                            )}
                        </ReloAccordionItem>
                    );
                })}
            </ReloAccordionList>
        </ReloAccordionContainer>
    );
};

const MultipleExpandAccordionReference = () => {
    const [openKeys, setOpenKeys] = React.useState<string[]>(['a']);
    const toggle = (key: string) =>
        setOpenKeys(prev =>
            prev.includes(key)
                ? prev.filter(candidate => candidate !== key)
                : [...prev, key],
        );

    return (
        <ReloAccordionContainer>
            <ReloAccordionList>
                {reloAccordionSections.map(section => {
                    const isActive = openKeys.includes(section.key);

                    return (
                        <ReloAccordionItem
                            key={section.key}
                            isActive={isActive}
                        >
                            <ReloAccordionTitle
                                onClick={() => toggle(section.key)}
                            >
                                {section.title}
                            </ReloAccordionTitle>
                            <ReloAccordionIcon
                                size="sm"
                                onClick={() => toggle(section.key)}
                            />
                            {isActive && (
                                <ReloAccordionChildItems>
                                    {section.body}
                                </ReloAccordionChildItems>
                            )}
                        </ReloAccordionItem>
                    );
                })}
            </ReloAccordionList>
        </ReloAccordionContainer>
    );
};

const DefaultOpenAccordionReference = () => {
    const [isActive, setIsActive] = React.useState(true);
    const toggle = () => setIsActive(prev => !prev);

    return (
        <ReloAccordionContainer>
            <ReloAccordionList>
                <ReloAccordionItem isActive={isActive}>
                    <ReloAccordionTitle onClick={toggle}>
                        Expanded by default
                    </ReloAccordionTitle>
                    <ReloAccordionIcon size="sm" onClick={toggle} />
                    {isActive && (
                        <ReloAccordionChildItems>
                            This panel starts open on first render.
                        </ReloAccordionChildItems>
                    )}
                </ReloAccordionItem>
            </ReloAccordionList>
        </ReloAccordionContainer>
    );
};

const DisabledAccordionReference = () => (
    <ReloAccordionContainer>
        <ReloAccordionList>
            <ReloAccordionItem
                isActive={false}
                style={{ opacity: 0.5, pointerEvents: 'none' }}
            >
                <ReloAccordionTitle>Disabled section</ReloAccordionTitle>
                <ReloAccordionIcon size="sm" />
            </ReloAccordionItem>
        </ReloAccordionList>
    </ReloAccordionContainer>
);

const IconAccordionReference = () => {
    const [isActive, setIsActive] = React.useState(false);
    const toggle = () => setIsActive(prev => !prev);

    return (
        <ReloAccordionContainer>
            <ReloAccordionList>
                <ReloAccordionItem isActive={isActive}>
                    <ReloAccordionTitle onClick={toggle}>
                        Toggle to rotate the caret
                    </ReloAccordionTitle>
                    <ReloAccordionIcon size="sm" onClick={toggle} />
                    {isActive && (
                        <ReloAccordionChildItems>
                            The caret rotates when the item is active.
                        </ReloAccordionChildItems>
                    )}
                </ReloAccordionItem>
            </ReloAccordionList>
        </ReloAccordionContainer>
    );
};

const NestedChildContentAccordionReference = () => {
    const [outerOpen, setOuterOpen] = React.useState(true);
    const [innerOpen, setInnerOpen] = React.useState(false);

    return (
        <ReloAccordionContainer>
            <ReloAccordionList>
                <ReloAccordionItem isActive={outerOpen}>
                    <ReloAccordionTitle
                        onClick={() => setOuterOpen(prev => !prev)}
                    >
                        Parent section
                    </ReloAccordionTitle>
                    <ReloAccordionIcon
                        size="sm"
                        onClick={() => setOuterOpen(prev => !prev)}
                    />
                    {outerOpen && (
                        <ReloAccordionChildItems>
                            <ReloAccordionContainer>
                                <ReloAccordionList>
                                    <ReloAccordionItem isActive={innerOpen}>
                                        <ReloAccordionTitle
                                            onClick={() =>
                                                setInnerOpen(prev => !prev)
                                            }
                                        >
                                            Nested child
                                        </ReloAccordionTitle>
                                        <ReloAccordionIcon
                                            size="sm"
                                            onClick={() =>
                                                setInnerOpen(prev => !prev)
                                            }
                                        />
                                        {innerOpen && (
                                            <ReloAccordionChildItems>
                                                Nested content inside the child
                                                items.
                                            </ReloAccordionChildItems>
                                        )}
                                    </ReloAccordionItem>
                                </ReloAccordionList>
                            </ReloAccordionContainer>
                        </ReloAccordionChildItems>
                    )}
                </ReloAccordionItem>
            </ReloAccordionList>
        </ReloAccordionContainer>
    );
};

const uiCatalogSections: UiCatalogSection[] = [
    {
        name: 'Button',
        description:
            'MVC-native action primitive compared against the raw Ant Design Button it replaces.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline button rendering.',
                Example: DefaultButtonExample,
                ReferenceExample: DefaultButtonReference,
            },
            {
                name: 'Disabled',
                description: 'Disabled action state.',
                Example: DisabledButtonExample,
                ReferenceExample: DisabledButtonReference,
            },
            {
                name: 'Loading',
                description: 'Async action state.',
                Example: LoadingButtonExample,
                ReferenceExample: LoadingButtonReference,
            },
            {
                name: 'Danger',
                description: 'Destructive semantic variant.',
                Example: DangerButtonExample,
                ReferenceExample: DangerButtonReference,
            },
            {
                name: 'Ghost',
                description: 'Low-emphasis variant on colored backgrounds.',
                Example: GhostButtonExample,
                ReferenceExample: GhostButtonReference,
            },
            {
                name: 'Long text',
                description: 'Long label wrapping and spacing check.',
                Example: LongTextButtonExample,
                ReferenceExample: LongTextButtonReference,
            },
        ],
    },
    {
        name: 'Card',
        description:
            'MVC-native generic content container compared against the raw Ant Design Card it replaces.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline grouped content.',
                Example: DefaultCardExample,
                ReferenceExample: DefaultCardReference,
            },
            {
                name: 'Titled',
                description: 'Optional title/header content.',
                Example: TitledCardExample,
                ReferenceExample: TitledCardReference,
            },
            {
                name: 'Action slot',
                description: 'Caller-owned action/footer content.',
                Example: ActionSlotCardExample,
                ReferenceExample: ActionSlotCardReference,
            },
            {
                name: 'Long content',
                description: 'Long body content spacing check.',
                Example: LongContentCardExample,
                ReferenceExample: LongContentCardReference,
            },
            {
                name: 'Loading compatible',
                description: 'Skeleton-compatible loading content.',
                Example: LoadingCompatibleCardExample,
                ReferenceExample: LoadingCompatibleCardReference,
            },
        ],
    },
    {
        name: 'Checkbox',
        description:
            'MVC-native selection control primitive compared against the raw Ant Design Checkbox it replaces.',
        examples: [
            {
                name: 'Unchecked',
                description: 'Baseline unchecked state.',
                Example: UncheckedCheckboxExample,
                ReferenceExample: UncheckedCheckboxReference,
            },
            {
                name: 'Checked',
                description: 'Checked state.',
                Example: CheckedCheckboxExample,
                ReferenceExample: CheckedCheckboxReference,
            },
            {
                name: 'Indeterminate',
                description: 'Mixed/partial selection state.',
                Example: IndeterminateCheckboxExample,
                ReferenceExample: IndeterminateCheckboxReference,
            },
            {
                name: 'Disabled',
                description: 'Disabled selection state.',
                Example: DisabledCheckboxExample,
                ReferenceExample: DisabledCheckboxReference,
            },
            {
                name: 'Group',
                description: 'Multi-select option group.',
                Example: GroupCheckboxExample,
                ReferenceExample: GroupCheckboxReference,
            },
        ],
    },
    {
        name: 'AlertBanner',
        description:
            'MVC-native persistent inline feedback primitive compared against the raw Ant Design Alert it replaces.',
        examples: [
            {
                name: 'Info',
                description: 'Informational inline feedback.',
                Example: InfoAlertBannerExample,
                ReferenceExample: InfoAlertBannerReference,
            },
            {
                name: 'Warning',
                description: 'Warning inline feedback.',
                Example: WarningAlertBannerExample,
                ReferenceExample: WarningAlertBannerReference,
            },
            {
                name: 'Error',
                description: 'Error inline feedback.',
                Example: ErrorAlertBannerExample,
                ReferenceExample: ErrorAlertBannerReference,
            },
            {
                name: 'Success',
                description: 'Success inline feedback.',
                Example: SuccessAlertBannerExample,
                ReferenceExample: SuccessAlertBannerReference,
            },
            {
                name: 'Long text',
                description: 'Title plus long description spacing check.',
                Example: LongContentAlertBannerExample,
                ReferenceExample: LongContentAlertBannerReference,
            },
            {
                name: 'Action',
                description: 'Caller-owned action content.',
                Example: ActionAlertBannerExample,
                ReferenceExample: ActionAlertBannerReference,
            },
        ],
    },
    {
        name: 'EmptyState',
        description:
            'MVC-native no-data primitive compared against the raw Ant Design Empty it replaces.',
        examples: [
            {
                name: 'No records',
                description: 'No records exist yet.',
                Example: NoRecordsEmptyStateExample,
                ReferenceExample: NoRecordsEmptyStateReference,
            },
            {
                name: 'No results',
                description: 'No results match current filters.',
                Example: NoSearchResultsEmptyStateExample,
                ReferenceExample: NoSearchResultsEmptyStateReference,
            },
            {
                name: 'No configured items',
                description: 'No configured items yet.',
                Example: NoConfiguredItemsEmptyStateExample,
                ReferenceExample: NoConfiguredItemsEmptyStateReference,
            },
            {
                name: 'Action slot',
                description: 'Caller-owned action slot.',
                Example: ActionSlotEmptyStateExample,
                ReferenceExample: ActionSlotEmptyStateReference,
            },
            {
                name: 'Message only',
                description: 'Message-only no-data state.',
                Example: MessageOnlyEmptyStateExample,
                ReferenceExample: MessageOnlyEmptyStateReference,
            },
        ],
    },
    {
        name: 'HeaderTabs',
        description:
            'MVC-native generic tab navigation primitive compared against the raw Ant Design Tabs it replaces.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline tab rendering.',
                Example: DefaultHeaderTabsExample,
                ReferenceExample: DefaultHeaderTabsReference,
            },
            {
                name: 'Active',
                description: 'Controlled active key with change handling.',
                Example: ActiveHeaderTabsExample,
                ReferenceExample: ActiveHeaderTabsReference,
            },
            {
                name: 'Disabled',
                description: 'Disabled tab state.',
                Example: DisabledHeaderTabsExample,
                ReferenceExample: DisabledHeaderTabsReference,
            },
            {
                name: 'Icons',
                description: 'Optional per-tab icons.',
                Example: IconHeaderTabsExample,
                ReferenceExample: IconHeaderTabsReference,
            },
            {
                name: 'Long label',
                description: 'Long tab label spacing check.',
                Example: LongLabelHeaderTabsExample,
                ReferenceExample: LongLabelHeaderTabsReference,
            },
        ],
    },
    {
        name: 'FormGroup',
        description:
            'MVC-native labeled form field group compared against the @relo/sports-components FormGroup family it replaces.',
        referenceLabel: 'Legacy @relo reference',
        examples: [
            {
                name: 'Label only',
                description: 'Label slot over a control.',
                Example: LabelOnlyFormGroupExample,
                ReferenceExample: LabelOnlyFormGroupReference,
            },
            {
                name: 'Label and hint',
                description: 'Label, control, and hint slots.',
                Example: LabelAndHintFormGroupExample,
                ReferenceExample: LabelAndHintFormGroupReference,
            },
            {
                name: 'Required',
                description: 'Required field marker.',
                Example: RequiredFormGroupExample,
                ReferenceExample: RequiredFormGroupReference,
            },
            {
                name: 'Error',
                description: 'Danger context with validation hint.',
                Example: ErrorFormGroupExample,
                ReferenceExample: ErrorFormGroupReference,
            },
            {
                name: 'Composed field',
                description: 'Full label/control/hint composition.',
                Example: ComposedFieldFormGroupExample,
                ReferenceExample: ComposedFieldFormGroupReference,
            },
        ],
    },
    {
        name: 'Select',
        description:
            'MVC-native option selection primitive compared against the raw Ant Design Select it replaces.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline single-value selection.',
                Example: DefaultSelectExample,
                ReferenceExample: DefaultSelectReference,
            },
            {
                name: 'Multiple',
                description: 'Multi-value selection mode.',
                Example: MultipleSelectExample,
                ReferenceExample: MultipleSelectReference,
            },
            {
                name: 'Disabled',
                description: 'Disabled selection state.',
                Example: DisabledSelectExample,
                ReferenceExample: DisabledSelectReference,
            },
            {
                name: 'Loading',
                description: 'Loading state while options resolve.',
                Example: LoadingSelectExample,
                ReferenceExample: LoadingSelectReference,
            },
            {
                name: 'Long option list',
                description: 'Long searchable option list.',
                Example: LongOptionListSelectExample,
                ReferenceExample: LongOptionListSelectReference,
            },
            {
                name: 'Empty',
                description: 'No options available.',
                Example: EmptySelectExample,
                ReferenceExample: EmptySelectReference,
            },
        ],
    },
    {
        name: 'LoadingDots',
        description:
            'MVC-native indeterminate loading indicator built on AntD Spin with a custom dots indicator.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline md dots.',
                Example: DefaultLoadingDotsExample,
            },
            {
                name: 'Small',
                description: 'Compact/inline scale.',
                Example: SmallLoadingDotsExample,
            },
            {
                name: 'Large',
                description: 'Section/panel-level scale.',
                Example: LargeLoadingDotsExample,
            },
            {
                name: 'White',
                description: 'White context on a dark background.',
                Example: WhiteLoadingDotsExample,
            },
            {
                name: 'Inline',
                description: 'Inline alongside text content.',
                Example: InlineLoadingDotsExample,
            },
        ],
    },
    {
        name: 'Spin',
        description:
            'MVC-native general-purpose loading spinner compared against the raw Ant Design Spin it wraps.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline spinner.',
                Example: DefaultSpinExample,
                ReferenceExample: DefaultSpinReference,
            },
            {
                name: 'Small',
                description: 'Compact spinner size.',
                Example: SmallSpinExample,
                ReferenceExample: SmallSpinReference,
            },
            {
                name: 'Large',
                description: 'Section/panel-level spinner size.',
                Example: LargeSpinExample,
                ReferenceExample: LargeSpinReference,
            },
            {
                name: 'Tip',
                description:
                    'Spinner with loading text in the wrapped pattern.',
                Example: TipSpinExample,
                ReferenceExample: TipSpinReference,
            },
            {
                name: 'Wrapped content',
                description: 'Spinning over blurred wrapped content.',
                Example: WrappedContentSpinExample,
                ReferenceExample: WrappedContentSpinReference,
            },
        ],
    },
    {
        name: 'SearchSelect',
        description:
            'MVC-native searchable single-select primitive compared against the raw Ant Design Select it replaces.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline searchable select.',
                Example: DefaultSearchSelectExample,
                ReferenceExample: DefaultSearchSelectReference,
            },
            {
                name: 'Search filter',
                description: 'Type-to-filter with a preselected value.',
                Example: SearchFilterSearchSelectExample,
                ReferenceExample: SearchFilterSearchSelectReference,
            },
            {
                name: 'No results',
                description: 'Empty options / no matches state.',
                Example: NoResultsSearchSelectExample,
                ReferenceExample: NoResultsSearchSelectReference,
            },
            {
                name: 'Disabled',
                description: 'Disabled select state.',
                Example: DisabledSearchSelectExample,
                ReferenceExample: DisabledSearchSelectReference,
            },
            {
                name: 'Loading',
                description: 'Async option loading state.',
                Example: LoadingSearchSelectExample,
                ReferenceExample: LoadingSearchSelectReference,
            },
        ],
    },
    {
        name: 'DateRangePicker',
        description:
            'MVC-native date range picker built on AntD RangePicker. Each bound is stored as fixed (a calendar date) or dynamic (days from Today) and re-resolved on load.',
        examples: [
            {
                name: 'Preset',
                description: 'Last 7 Days, resolved against Today.',
                Example: DefaultDateRangePickerExample,
            },
            {
                name: 'Ends Today',
                description: 'Fixed start with a dynamic Today end.',
                Example: EndTodayDateRangePickerExample,
            },
            {
                name: 'Fixed',
                description: 'Both bounds are calendar dates.',
                Example: FixedDateRangePickerExample,
            },
            {
                name: 'Custom presets',
                description: 'Caller-provided preset list.',
                Example: CustomPresetsDateRangePickerExample,
            },
            {
                name: 'Empty',
                description: 'No range selected yet.',
                Example: EmptyDateRangePickerExample,
            },
            {
                name: 'Disabled',
                description: 'Disabled picker.',
                Example: DisabledDateRangePickerExample,
            },
        ],
    },
    {
        name: 'SlabNav',
        description:
            'MVC-native slab-navigation primitive set built on AntD Menu. The default (legacy tone) is a visual drop-in for the legacy @relo/sports-components SlabNav shown in the reference column; tone="brand" opts into the lighter look.',
        referenceLabel: 'Legacy @relo reference',
        examples: [
            {
                name: 'Default',
                description:
                    'Baseline icon-plus-label slab; default legacy tone matches the @relo reference.',
                Example: DefaultSlabNavExample,
                ReferenceExample: DefaultSlabNavReference,
            },
            {
                name: 'Active item',
                description: 'Controlled active item with selection handling.',
                Example: ActiveSlabNavExample,
                ReferenceExample: ActiveSlabNavReference,
            },
            {
                name: 'Disabled',
                description:
                    'Non-interactive/disabled item alongside selected.',
                Example: DisabledSlabNavExample,
                ReferenceExample: DisabledSlabNavReference,
            },
            {
                name: 'Many items',
                description: 'Dense list spacing and selection check.',
                Example: ManyItemsSlabNavExample,
                ReferenceExample: ManyItemsSlabNavReference,
            },
            {
                name: 'Brand tone',
                description:
                    'Opt-in light brand variant (tone="brand"); no legacy equivalent.',
                Example: BrandToneSlabNavExample,
            },
        ],
    },
    {
        name: 'Tooltip',
        description:
            'MVC-native hover/focus popup primitive compared against the raw Ant Design Tooltip it replaces.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline hover tooltip on a trigger.',
                Example: DefaultTooltipExample,
                ReferenceExample: DefaultTooltipReference,
            },
            {
                name: 'Placements',
                description: 'Top, right, bottom, and left placements.',
                Example: PlacementsTooltipExample,
                ReferenceExample: PlacementsTooltipReference,
            },
            {
                name: 'Disabled trigger',
                description: 'Tooltip over a wrapped disabled control.',
                Example: DisabledTriggerTooltipExample,
                ReferenceExample: DisabledTriggerTooltipReference,
            },
            {
                name: 'Long content',
                description: 'Longer wrapping tooltip content.',
                Example: LongContentTooltipExample,
                ReferenceExample: LongContentTooltipReference,
            },
        ],
    },
    {
        name: 'Tag',
        description:
            'MVC-native status/category label primitive compared against the raw Ant Design Tag it replaces.',
        examples: [
            {
                name: 'Default',
                description: 'Baseline tag rendering.',
                Example: DefaultTagExample,
                ReferenceExample: DefaultTagReference,
            },
            {
                name: 'Colors / status',
                description: 'Preset color and status variants.',
                Example: ColorsTagExample,
                ReferenceExample: ColorsTagReference,
            },
            {
                name: 'Closable',
                description: 'Removable tag with a close affordance.',
                Example: ClosableTagExample,
                ReferenceExample: ClosableTagReference,
            },
            {
                name: 'Long text',
                description: 'Long label wrapping check.',
                Example: LongTextTagExample,
                ReferenceExample: LongTextTagReference,
            },
        ],
    },
    {
        name: 'Accordion',
        description:
            'MVC-native accordion family (Container/List/Item/Title/Icon/ChildItems) driven by the AntD Collapse theme token, replacing the legacy @relo gds-accordion shown in the reference column. Caller-owned open state.',
        referenceLabel: 'Legacy @relo reference',
        examples: [
            {
                name: 'Single expand',
                description: 'Single-open: opening one item closes the others.',
                Example: SingleExpandAccordionExample,
                ReferenceExample: SingleExpandAccordionReference,
            },
            {
                name: 'Multiple expand',
                description: 'Each item toggles independently.',
                Example: MultipleExpandAccordionExample,
                ReferenceExample: MultipleExpandAccordionReference,
            },
            {
                name: 'Default open',
                description: 'One item expanded on first render.',
                Example: DefaultOpenAccordionExample,
                ReferenceExample: DefaultOpenAccordionReference,
            },
            {
                name: 'Disabled',
                description: 'Non-interactive/disabled item.',
                Example: DisabledAccordionExample,
                ReferenceExample: DisabledAccordionReference,
            },
            {
                name: 'Icon',
                description: 'Caret rotates between collapsed and expanded.',
                Example: IconAccordionExample,
                ReferenceExample: IconAccordionReference,
            },
            {
                name: 'Nested child content',
                description: 'Child items host a nested accordion.',
                Example: NestedChildContentAccordionExample,
                ReferenceExample: NestedChildContentAccordionReference,
            },
        ],
    },
];

export default uiCatalogSections;
